import { useEffect, useRef, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { useKeyboardState } from "./useKeyboardState";
import { visualLog } from "@/components/debug/VisualDebugger";
import { getDeviceInfo } from "@/utils/deviceInfo";

const describeElement = (el: HTMLElement) => {
  const id = el.id ? `#${el.id}` : "";
  const classes = el.className
    ? `.${el.className.toString().replace(/\s+/g, ".")}`
    : "";
  return `${el.tagName}${id}${classes}`;
};

export interface ScrollOptions {
  behavior?: "smooth" | "instant";
  block?: "start" | "center" | "end" | "nearest";
  inline?: "start" | "center" | "end" | "nearest";
  offsetTop?: number;
  offsetBottom?: number;
  /**
   * Posizione verticale desiderata dell'elemento con focus
   * espressa come percentuale dell'altezza visibile (0-1).
   * Di default l'elemento viene posizionato al 30% dall'alto.
   */
  focusPosition?: number;
  /**
   * Soglia per considerare la tastiera aperta rispetto all'altezza originale
   * 0.5 significa tastiera aperta se l'altezza si riduce del 50%
   */
  keyboardThreshold?: number;
}

/**
 * Hook avanzato per gestire lo scrolling quando la tastiera virtuale è aperta.
 * Assicura che gli elementi con focus rimangano sempre visibili e gestisce
 * il contenitore per permettere scroll completo della pagina.
 */
export const useKeyboardAwareScrolling = (
  containerRef?: React.RefObject<HTMLElement>,
  options: ScrollOptions = {}
) => {
  const { keyboardThreshold } = options;
  const { isKeyboardOpen } = useKeyboardState(keyboardThreshold);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const keyboardOpenRef = useRef(false);
  const lastKeyboardTransition = useRef({
    state: isKeyboardOpen,
    timestamp: Date.now(),
  });

  useEffect(() => {
    const info = getDeviceInfo();
    visualLog.info(
      `Scroll hook device ${info.model || "n/a"} Android ${
        info.androidVersion
      } OneUI ${info.oneUIVersion}`
    );
    if (Capacitor.isPluginAvailable("Keyboard")) {
      Keyboard.getResizeMode()
        .then((mode) => {
          visualLog.info(`Keyboard resize mode: ${mode.mode}`);
        })
        .catch(() => {});
    }

    if ((Capacitor as any).isPluginAvailable?.("GestureNavigation")) {
      (Capacitor as any).Plugins?.GestureNavigation?.isEnabled?.()
        .then((res: any) => {
          visualLog.info(`Gesture navigation: ${res?.enabled}`);
        })
        .catch(() => {});
    }
  }, []);

  const {
    behavior = "smooth",
    block = "center",
    inline = "nearest",
    offsetTop,
    offsetBottom,
    focusPosition = 0.3,
  } = options;

  // Funzione per scrollare un elemento in vista considerando la tastiera
  const scrollElementIntoView = useCallback(
    (element: HTMLElement, immediate = false) => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      const baseContainer = containerRef?.current as HTMLElement | undefined;
      const getScrollableContainer = (startEl: HTMLElement): HTMLElement => {
        let current: HTMLElement | null = startEl.parentElement;
        while (current && current !== document.documentElement) {
          const style = window.getComputedStyle(current);
          const overflowY = style.overflowY;
          const isScrollable =
            overflowY === "auto" ||
            overflowY === "scroll" ||
            current.scrollHeight > current.clientHeight;
          if (isScrollable) return current;
          current = current.parentElement;
        }
        return document.documentElement;
      };

      let container: HTMLElement =
        baseContainer || getScrollableContainer(element);
      if (
        baseContainer &&
        baseContainer.scrollHeight <= baseContainer.clientHeight
      ) {
        container = getScrollableContainer(element);
      }

      const delay = immediate ? 0 : isKeyboardOpen ? 150 : 50;

      scrollTimeoutRef.current = setTimeout(() => {
        const elapsed = Date.now() - lastKeyboardTransition.current.timestamp;
        visualLog.info(
          `Scroll attempt for ${describeElement(element)} in ${describeElement(
            container
          )} ` +
            `(keyboard ${
              lastKeyboardTransition.current.state ? "open" : "closed"
            } ` +
            `${elapsed}ms ago)`
        );
        try {
          const elementRect = element.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();

          // Calcola la posizione relativa dell'elemento nel container
          const elementTop =
            elementRect.top - containerRect.top + container.scrollTop;
          const elementBottom = elementTop + elementRect.height;

          const containerHeight = container.clientHeight;
          const topPadding = offsetTop ?? containerHeight * focusPosition;
          const bottomPadding =
            offsetBottom ?? containerHeight * (1 - focusPosition);

          // Calcola l'area visibile considerando la tastiera
          const visibleTop = container.scrollTop + topPadding;
          const visibleBottom =
            container.scrollTop + containerHeight - bottomPadding;

          let targetScrollTop = container.scrollTop;

          // Se l'elemento è sopra l'area visibile
          if (elementTop < visibleTop) {
            targetScrollTop = elementTop - topPadding;
          }
          // Se l'elemento è sotto l'area visibile
          else if (elementBottom > visibleBottom) {
            targetScrollTop = elementBottom - containerHeight + bottomPadding;
          }

          // Assicura che lo scroll resti nei limiti consentiti
          targetScrollTop = Math.max(
            0,
            Math.min(targetScrollTop, container.scrollHeight - containerHeight)
          );

          if (Math.abs(targetScrollTop - container.scrollTop) > 10) {
            if (behavior === "smooth" && container.scrollTo) {
              container.scrollTo({
                top: targetScrollTop,
                behavior: "smooth",
              });
            } else {
              container.scrollTop = targetScrollTop;
            }

            visualLog.info(
              `Scroll success for ${describeElement(
                element
              )} using ${describeElement(container)} -> ${targetScrollTop}px`
            );
          }

          // Fallback: usa scrollIntoView nativo se il calcolo manuale fallisce
          if (container === document.documentElement) {
            visualLog.warn(
              `Fallback scrollIntoView for ${describeElement(
                element
              )} in ${describeElement(container)}`
            );
            element.scrollIntoView({
              behavior,
              block,
              inline,
            });
          }
        } catch (error) {
          console.warn("Error in scrollElementIntoView:", error);
          visualLog.error(
            `Scroll error on ${describeElement(element)}: ${String(error)}`
          );
          // Fallback al comportamento nativo
          element.scrollIntoView({ behavior, block, inline });
        }
      }, delay);
    },
    [
      isKeyboardOpen,
      containerRef,
      behavior,
      block,
      inline,
      offsetTop,
      offsetBottom,
      focusPosition,
    ]
  );

  // Gestisce il focus degli elementi con data-keyboard-scroll
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.dataset.keyboardScroll !== undefined) {
        lastFocusedRef.current = target;
        visualLog.info(
          `Element focused: ${describeElement(target)} in ${describeElement(
            containerRef?.current || document.documentElement
          )}`
        );
        scrollElementIntoView(target);
      }
    };

    const handleBlur = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target === lastFocusedRef.current) {
        visualLog.info(
          `Element blurred: ${describeElement(target)} in ${describeElement(
            containerRef?.current || document.documentElement
          )}`
        );
      }
    };

    document.addEventListener("focusin", handleFocus);
    document.addEventListener("focusout", handleBlur);

    return () => {
      document.removeEventListener("focusin", handleFocus);
      document.removeEventListener("focusout", handleBlur);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [scrollElementIntoView]);

  // Ri-posiziona l'elemento quando la tastiera si apre/chiude
  useEffect(() => {
    // Traccia i cambiamenti dello stato della tastiera
    if (keyboardOpenRef.current !== isKeyboardOpen) {
      keyboardOpenRef.current = isKeyboardOpen;
      lastKeyboardTransition.current = {
        state: isKeyboardOpen,
        timestamp: Date.now(),
      };

      if (isKeyboardOpen && lastFocusedRef.current) {
        visualLog.info(
          `Keyboard opened - repositioning focused element in ${describeElement(
            containerRef?.current || document.documentElement
          )}`
        );
        scrollElementIntoView(lastFocusedRef.current, true);
      } else if (!isKeyboardOpen && lastFocusedRef.current) {
        visualLog.info(
          `Keyboard closed - repositioning focused element in ${describeElement(
            containerRef?.current || document.documentElement
          )}`
        );
        // Quando la tastiera si chiude, aspetta un po' di più per la transizione
        setTimeout(() => {
          if (lastFocusedRef.current) {
            scrollElementIntoView(lastFocusedRef.current, true);
          }
        }, 300);
      }
    }
  }, [isKeyboardOpen, scrollElementIntoView]);

  // Gestisce i cambiamenti di altezza del viewport
  useEffect(() => {
    const handleViewportChange = (e: CustomEvent) => {
      if (lastFocusedRef.current) {
        visualLog.info(
          `Viewport height changed: ${
            e.detail.height
          }px - repositioning in ${describeElement(
            containerRef?.current || document.documentElement
          )}`
        );
        scrollElementIntoView(lastFocusedRef.current, true);
      }
    };

    window.addEventListener(
      "viewport-height-changed",
      handleViewportChange as EventListener
    );

    return () => {
      window.removeEventListener(
        "viewport-height-changed",
        handleViewportChange as EventListener
      );
    };
  }, [scrollElementIntoView]);

  // Applica le classi CSS al container se fornito
  useEffect(() => {
    if (containerRef?.current) {
      const container = containerRef.current;
      container.classList.add("keyboard-aware-scroll");

      return () => {
        container.classList.remove("keyboard-aware-scroll");
      };
    }
  }, [containerRef]);

  return {
    isKeyboardOpen,
    lastFocusedElement: lastFocusedRef.current,
    scrollElementIntoView,
  };
};
